/**
 * AeonBriefSlot — reusable render slot for a single Aeon brief (Cycle 081).
 *
 * Displays a brief's narrative text, freshness timestamp, and skill provenance label.
 * If brief is null, renders nothing (not a placeholder, not a spinner).
 * Parent layout tolerates the slot being absent.
 *
 * Designed for reuse in the investigation view Zone 1 (future cycle).
 */

import { useState } from 'react';
import { cn } from '../../lib/utils';
import type { AeonBrief } from '../../hooks/useIntelligenceSynthesis';
import { CalibrationDisplay } from './intel/CalibrationDisplay';

interface AeonBriefSlotProps {
  brief: AeonBrief | null;
  /** "hero" renders with slightly more visual weight; "inline" is subdued. */
  variant?: 'hero' | 'inline';
  className?: string;
}

/**
 * Map AeonBrief.sourceId (e.g. "aeon_narrative") → backend skill_id
 * (e.g. "echelon-narrative") so the per-skill calibration row can be
 * surfaced in the expansion. Mirror of brief_synthesizer.SKILL_CONFIGS.
 */
const SKILL_ID_BY_SOURCE: Record<string, string> = {
  aeon_narrative: 'echelon-narrative',
  aeon_market_scan: 'echelon-market-scan',
  aeon_theatre_brief: 'echelon-theatre-brief',
  aeon_summary: 'echelon-summary',
};

function formatAge(collectedAt: string): string {
  const diff = Date.now() - new Date(collectedAt).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const PROVENANCE_LABELS: Record<string, string> = {
  aeon_market_scan: 'Market Scan',
  aeon_narrative: 'Narrative',
  aeon_theatre_brief: 'Theatre Brief',
  aeon_generic: 'Aeon',
};

export function AeonBriefSlot({ brief, variant = 'hero', className }: AeonBriefSlotProps) {
  const [showCalibration, setShowCalibration] = useState(false);

  if (!brief) return null;

  const isHero = variant === 'hero';
  const provenance = PROVENANCE_LABELS[brief.sourceId] ?? 'Aeon';
  const skillId = SKILL_ID_BY_SOURCE[brief.sourceId];

  return (
    <div
      className={cn(
        'border-t border-dashed border-[var(--e-border-secondary)]/30',
        isHero ? 'px-3 py-3' : 'px-2 py-1.5',
        className,
      )}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className={cn(
          'px-1.5 py-0.5 text-[7px] font-mono uppercase tracking-widest rounded-sm',
          'bg-[var(--e-bg-elevated)]/60 text-[var(--e-text-muted)]/80',
          'border border-dashed border-[var(--e-border-secondary)]/40',
        )}>
          {provenance}
        </span>
        <span className="text-[8px] font-mono text-[var(--e-text-muted)]/50">
          {formatAge(brief.collectedAt)}
        </span>
        {skillId && (
          <button
            type="button"
            onClick={() => setShowCalibration((s) => !s)}
            aria-expanded={showCalibration}
            aria-controls={`calibration-${skillId}`}
            data-testid="aeon-brief-calibration-toggle"
            className="ml-auto px-1.5 py-0.5 text-[7px] font-mono uppercase tracking-widest rounded-sm
              border border-[var(--e-border-secondary)]/40 text-[var(--e-text-muted)]/80
              hover:text-[var(--e-text-primary)] hover:bg-[var(--e-bg-elevated)]
              focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--e-purple-400)]"
          >
            {showCalibration ? '− Calibration' : '+ Calibration'}
          </button>
        )}
      </div>
      {/* Brief text: sans-serif prose for readability. Hero variant uses
          larger type for investigation Zone 1 narrative context. */}
      <p className={cn(
        'text-[var(--e-text-secondary)] max-w-[640px]',
        isHero
          ? 'text-[15px] leading-[1.6] font-normal'
          : 'text-[12px] leading-[1.5] font-normal',
      )}>
        {brief.summaryText}
      </p>
      {showCalibration && skillId && (
        <div id={`calibration-${skillId}`} className="mt-2">
          <CalibrationDisplay skillId={skillId} />
        </div>
      )}
    </div>
  );
}
